/**
 * @fileoverview MandatoryDisclaimer Component - Required profile completion modal
 * 
 * Provides a mandatory profile completion interface for new users:
 * - Profile data validation and completion
 * - Photo upload functionality
 * - Form validation with real-time feedback
 * - Member cohort selection
 * - Years of experience validation
 * - Toast notifications for user feedback
 * - Prevents site access until profile is complete
 * - Integration with Auth0 user data
 * 
 * @component
 * @example
 * <MandatoryDisclaimer 
 *   show={showModal} 
 *   onComplete={handleComplete} 
 * />
 * 
 * @requires React
 * @requires @auth0/auth0-react
 * @requires ../../utils/apiHelper
 * @requires react-toastify
 * @requires ./MandatoryDisclaimer.css
 * 
 * @author By John Bellew - Infinia.ie - last updated 10/07/2025
 */

import React, { useState, useEffect, useRef, useContext } from 'react';
import apiHelper from '../../utils/apiHelper';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './MandatoryDisclaimer.css';
import { AuthContext } from '../Auth/AuthContext';

/**
 * MandatoryDisclaimer Component
 * 
 * Renders a mandatory profile completion modal that prevents site access until
 * required profile information is provided by the user.
 * 
 * Features:
 * - Required profile field validation
 * - Photo upload with preview
 * - Member cohort selection
 * - Years of experience validation
 * - Real-time form validation
 * - Toast notifications for feedback
 * - Pre-filled existing profile data
 * - Responsive form layout
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {boolean} props.show - Whether to show the modal
 * @param {Function} props.onComplete - Callback when profile is completed
 * 
 * @returns {JSX.Element|null} Mandatory profile completion modal or null if hidden
 */
const MandatoryDisclaimer = ({ show, onComplete }) => {
    const {
      user: [user],
    } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Photo upload state
  const [profileImage, setProfileImage] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    jobTitle: '',
    company: '',
    memberCohort: '',
    NoOfYrsintheIndustry: '',
    linkedInProfile: '',
    bio: ''
  });
  
  const [errors, setErrors] = useState({});
  const [yearsError, setYearsError] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      if (user?.profile?.email && show) {
        try {
          const profileData = await apiHelper.getMemberProfileByEmail(user.profile.email);
          setProfile(profileData);
          
          // Pre-fill form with existing data
          setFormData({
            name: profileData.name || '',
            jobTitle: profileData.jobTitle || '',
            company: profileData.company || '',
            memberCohort: profileData.memberCohort || '',
            NoOfYrsintheIndustry: profileData.NoOfYrsintheIndustry || '',
            linkedInProfile: profileData.linkedInProfile || '',
            bio: profileData.bio || ''
          });
          
          // Check if profile is complete
          const isComplete = profileData.name &&
                            profileData.jobTitle && 
                            profileData.company && 
                            profileData.memberCohort && 
                            profileData.NoOfYrsintheIndustry;
          if (isComplete) {
            // Profile complete - shouldn't show modal
            onComplete();
            return;
          }
          
        } catch (error) {
          console.error('Error fetching profile:', error);
        }
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user?.profile?.email, show]);

  // Load profile image - adapted from MemberProfile.js
  useEffect(() => {
    const loadProfileImage = async () => {
      if (!profile?.photo) return;

      try {
        const imageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/profile-images/${profile.photo}`;
        const authToken = await apiHelper.getAuthToken();
        
        const response = await fetch(imageUrl, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (!response.ok) {
          if (response.status !== 404) {
            console.error('Error loading profile image:', response.statusText);
          }
          setProfileImage(null);
          return;
        }

        const blob = await response.blob();
        const imageObjectUrl = URL.createObjectURL(blob);
        setProfileImage(imageObjectUrl);
      } catch (error) {
        if (error.message && !error.message.includes('404')) {
          console.error('Error loading profile image:', error);
        }
        setProfileImage(null);
      }
    };

    loadProfileImage();

    return () => {
      if (profileImage) {
        URL.revokeObjectURL(profileImage);
      }
    };
  }, [profile?.photo]);

  // Handle image upload - adapted from MemberProfile.js
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setImageUploading(true);
      setUploadError(null);

      await apiHelper.uploadMemberProfileImage(file, profile.id);
      
      // Get fresh profile data
      const freshProfileData = await apiHelper.getMemberProfileByEmail(user.profile.email);
      setProfile(freshProfileData);

      toast.success('Photo uploaded successfully!');
      
      // Dispatch event for any listeners
      window.dispatchEvent(new CustomEvent('profileImageChanged'));
    } catch (err) {
      console.error('Error uploading image:', err);
      setUploadError('Failed to upload photo. Please try again.');
      toast.error('Failed to upload photo.');
    } finally {
      setImageUploading(false);
    }
  };

  // Same validation function as MemberProfile
  const validateYears = (years) => {
    if (years === '' || years === null || years === undefined) return ''; // Allow empty/null/undefined values
    const numYears = Number(years);
    if (isNaN(numYears)) return 'Please enter a valid number';
    if (numYears < 0) return 'Years cannot be negative';
    if (numYears > 100) return 'Please enter a realistic number of years';
    if (!Number.isInteger(numYears)) return 'Please enter a whole number';
    return ''; // validation passed
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }
    
    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    }
    
    if (!formData.memberCohort.trim()) {
      newErrors.memberCohort = 'Member cohort is required';
    }
    
    // Validate years using the same logic as MemberProfile
    const yearsValidationError = validateYears(formData.NoOfYrsintheIndustry);
    if (!formData.NoOfYrsintheIndustry || formData.NoOfYrsintheIndustry === '' || formData.NoOfYrsintheIndustry === null) {
      newErrors.NoOfYrsintheIndustry = 'Years in industry is required';
    } else if (yearsValidationError) {
      newErrors.NoOfYrsintheIndustry = yearsValidationError;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is valid for button state
  const isFormValid = () => {
    return formData.name.trim() &&
           formData.jobTitle.trim() && 
           formData.company.trim() && 
           formData.memberCohort.trim() && 
           formData.NoOfYrsintheIndustry && 
           formData.NoOfYrsintheIndustry !== '' && 
           formData.NoOfYrsintheIndustry !== null &&
           !validateYears(formData.NoOfYrsintheIndustry);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleProfileSubmit = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      await apiHelper.updateProfile({
        id: profile.id,
        name: formData.name,
        linkedInProfile: formData.linkedInProfile,
        jobTitle: formData.jobTitle,
        NoOfYrsintheIndustry: formData.NoOfYrsintheIndustry,
        fieldVisibility: profile.fieldVisibility,
        bio: formData.bio,
        workingGroupRole: profile.workingGroupRole,
        company: formData.company,
        memberCohort: formData.memberCohort
      });
      
      onComplete(); // Profile complete, allow access to site
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile. Please try again.');
    }
    setSaving(false);
  };

  // ProfileImage component adapted for modal context
  const ProfileImageUpload = () => {
    const handleImageClick = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    return (
      <div className="profile-photo-section">
        <label>Profile Photo (Optional)</label>
        <div className="photo-upload-container">
          {profileImage ? (
            <div className="photo-preview" onClick={handleImageClick}>
              <img
                src={profileImage}
                alt={profile?.name || 'Profile'}
                className="preview-image"
              />
              <div className="photo-overlay">
                <i className="fas fa-camera"></i>
                <span>{imageUploading ? 'Uploading...' : 'Change Photo'}</span>
              </div>
            </div>
          ) : (
            <div className="photo-placeholder" onClick={handleImageClick}>
              <div className="placeholder-content">
                <i className="fas fa-user-circle"></i>
                <span>{imageUploading ? 'Uploading...' : 'Add Photo'}</span>
              </div>
            </div>
          )}
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            style={{ display: 'none' }}
          />
          
          {uploadError && (
            <div className="upload-error-message">
              <i className="fas fa-exclamation-circle"></i>
              {uploadError}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!show || loading) return null;

  return (
    <>
      <ToastContainer />
      <div className="mandatory-disclaimer-overlay">
        <div className="mandatory-disclaimer-modal">
          <div className="disclaimer-header">
            <div className="disclaimer-logo">
              <img 
                src="/assets/img/daire-if-logo.png" 
                alt="Irish Funds" 
                style={{ height: '40px' }}
              />
            </div>
            <h2>Complete Your Profile</h2>
          </div>

          <div className="disclaimer-content">
            <div className="profile-step">
              <h3>Welcome to Irish Funds Member Portal</h3>
              <p className="step-description">
                Please complete the required information below to access the member portal.
              </p>
              
              {/* Photo Upload Section */}
              <ProfileImageUpload />
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={errors.name ? 'error' : ''}
                    placeholder="e.g., John Smith"
                  />
                  {errors.name && <span className="error-text">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label>Job Title *</label>
                  <input
                    type="text"
                    value={formData.jobTitle}
                    onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                    className={errors.jobTitle ? 'error' : ''}
                    placeholder="e.g., Fund Manager"
                  />
                  {errors.jobTitle && <span className="error-text">{errors.jobTitle}</span>}
                </div>

                <div className="form-group">
                  <label>Company *</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className={errors.company ? 'error' : ''}
                    placeholder="e.g., ABC Asset Management"
                  />
                  {errors.company && <span className="error-text">{errors.company}</span>}
                </div>

                <div className="form-group">
                  <label>Member Cohort *</label>
                  <select
                    value={formData.memberCohort}
                    onChange={(e) => handleInputChange('memberCohort', e.target.value)}
                    className={errors.memberCohort ? 'error' : ''}
                  >
                    <option value="">Select a cohort</option>
                    <option value="Admin/Custodian">Admin/Custodian</option>
                    <option value="Asset manager">Asset manager</option>
                    <option value="Auditor">Auditor</option>
                    <option value="Innovation, Tech & Infrastructure">Innovation, Tech & Infrastructure</option>
                    <option value="Legal">Legal</option>
                    <option value="Irish Funds Employee">Irish Funds Employee</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.memberCohort && <span className="error-text">{errors.memberCohort}</span>}
                </div>

                <div className="form-group">
                  <label>Number of years in Funds Industry *</label>
                  <input
                    type="number"
                    value={formData.NoOfYrsintheIndustry}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : e.target.value;
                      const error = validateYears(value);
                      setYearsError(error);
                      handleInputChange('NoOfYrsintheIndustry', value);
                    }}
                    className={`${errors.NoOfYrsintheIndustry || yearsError ? 'error' : ''}`}
                    placeholder="Enter years in industry"
                    min="0"
                    max="100"
                    step="1"
                  />
                  {(errors.NoOfYrsintheIndustry || yearsError) && (
                    <span className="error-text">{errors.NoOfYrsintheIndustry || yearsError}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>LinkedIn Profile (Optional)</label>
                  <input
                    type="url"
                    value={formData.linkedInProfile}
                    onChange={(e) => handleInputChange('linkedInProfile', e.target.value)}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Bio (Optional)</label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    placeholder="Tell us about yourself..."
                    rows="3"
                  />
                </div>
              </div>

              <div className="step-actions">
                <button 
                  className="btn-primary" 
                  onClick={handleProfileSubmit}
                  disabled={saving || !isFormValid()}
                >
                  {saving ? 'Saving...' : 'Complete & Enter Portal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MandatoryDisclaimer; 